import React from "react";
import {
  Popover,
  Box,
  Typography,
  Button,
  List,
  ListItem
} from "@mui/material";
import {
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Notifications as DefaultIcon
} from "@material-ui/icons"; 
import { useTranslation } from "react-i18next";
import { useNotification, INotification } from "../../../../contexts/NotificationContext"; 
// Removed formatDistanceToNow
// Make sure to import your helper function if it lives in a separate utils file:
 import { getRelativeTimeAgo } from "../../../../helpers/utils"; 
import "./LivisNotification.scss";

interface NotificationProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onViewAllClick: () => void; 
}

const LivisNotificationMenu = ({ anchorEl, open, onClose, onViewAllClick }: NotificationProps) => {
  const { t } = useTranslation();
  const { headerNotifications, markAllAsRead } = useNotification();

  // --- Helper: Get Icon based on API type ---
  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckIcon className="icon-success" style={{ color: '#4CAF50' }} />;
      case 'error': return <ErrorIcon className="icon-error" style={{ color: '#F44336' }} />;
      case 'warning': return <WarningIcon className="icon-warning" style={{ color: '#FF9800' }} />;
      case 'info':
      default: return <InfoIcon className="icon-info" style={{ color: '#2196F3' }} />;
    }
  };

  // --- Helper: Get Background color for icon circle ---
  const getIconBgClass = (type: string) => {
    return 'blue-circle'; 
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disableScrollLock={true}
      PaperProps={{
        className: "livis-notification-popover",
        elevation: 0,
        sx: {
          mt: 1.5,
          overflow: 'visible',
          filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.1))',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'var(--livis-color-background-white)',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        }
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box className="livis-notif-wrapper">
        
        {/* Header */}
        <Box className="livis-notif-header">
          <Typography className="header-title">
            {t("Notifications")}
          </Typography>
          <Typography 
            className="mark-read-link" 
            onClick={() => markAllAsRead()}
            style={{ cursor: 'pointer' }}
          >
            {t("Mark all as read")}
          </Typography>
        </Box>

        {/* List */}
        <List className="livis-notif-list">
          {headerNotifications.length === 0 ? (
             <ListItem className="livis-notif-item">
                <Typography variant="body2" style={{ padding: 20, width: '100%', textAlign: 'center', color: '#999' }}>
                   {t("No new notifications")}
                </Typography>
             </ListItem>
          ) : (
            headerNotifications.map((item: any) => { 
              // BUG FIX: Check root 'is_read' property first
              const isRead = item.is_read !== undefined ? item.is_read : item.channel_status?.in_app?.is_read;

              return (
                <ListItem key={item._id} className="livis-notif-item" disableGutters>
                  <Box className="notif-content-block">
                    <Box className="notif-row">
                      
                      {/* Icon */}
                      <Box className={`notif-icon-circle ${getIconBgClass(item.notification_type)}`}>
                        {getIcon(item.notification_type)}
                      </Box>
                      
                      {/* Content */}
                      <Box className="notif-info">
                        <Typography className="notif-text">
                          <strong>{t(item.title)}</strong>
                          <br />
                          <span style={{ fontSize: '0.9em', color: '#666' }}>{item.message}</span>
                        </Typography>
                        
                        <Box className="notif-meta-row">
                          <Typography className="notif-time">
                             {/* <-- Applied the new date formatter helper here */}
                             {item.created_at ? getRelativeTimeAgo(item.created_at) : ''}
                          </Typography>
                          
                          {/* Display Read Status */}
                          {isRead && (
                              <span className="read-status">
                                {/* <-- Changed to DoneAllIcon for the double tick */}
                                <DoneAllIcon fontSize="inherit" style={{ marginRight: 4 }} /> {t("Read")}
                              </span>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })
          )}

          {/* Footer */}
          <ListItem className="livis-notif-footer-item">
             <Button 
                className="view-all-pill"
                onClick={() => {
                    if(onViewAllClick) onViewAllClick();
                    onClose();
                }}
             >
               {t("View all")}
             </Button>
          </ListItem>

        </List>
      </Box>
    </Popover>
  );
};

export default LivisNotificationMenu;